import { PushNotifications } from "@capacitor/push-notifications"
import { isNativeApp } from "@/lib/native"

/**
 * Asking for notifications, and telling the server where to send them.
 *
 * Runs from the native shell on launch. In a browser this does nothing — the
 * plugin has no web implementation and `isNativeApp()` is the guard.
 *
 * Deliberately NOT prompting on first launch. iOS gives an app exactly one
 * chance to ask: decline it and the only way back is Settings, which nobody
 * goes to. So this returns "ask-later" until something has earned the prompt —
 * a barber with a booking to be told about. Call it with `{ prompt: true }`
 * from that moment, not from app start.
 */

export type PushPermission = "granted" | "denied" | "ask-later"

export async function initPush({ prompt = false }: { prompt?: boolean } = {}): Promise<PushPermission> {
  if (!isNativeApp()) return "ask-later"

  try {
    const current = await PushNotifications.checkPermissions()

    if (current.receive === "denied") return "denied"

    if (current.receive !== "granted") {
      if (!prompt) return "ask-later"
      const asked = await PushNotifications.requestPermissions()
      if (asked.receive !== "granted") return "denied"
    }

    // Registration is what produces the token; the listener below receives it.
    await PushNotifications.register()
    return "granted"
  } catch (err) {
    console.warn("[push] init failed:", err)
    return "ask-later"
  }
}

/**
 * Wire the plugin's listeners. Call once, from the shell.
 *
 * Returns a teardown that removes them, so a hot reload in development does not
 * stack duplicate handlers.
 */
export async function attachPushListeners(opts: {
  /** Navigate the web view — the shell passes its router. */
  onOpen: (path: string) => void
  appVersion?: string
}): Promise<() => void> {
  if (!isNativeApp()) return () => {}

  const handles: { remove: () => Promise<void> }[] = []

  try {
    handles.push(
      await PushNotifications.addListener("registration", async ({ value }) => {
        // Fires on every register(), including when APNs rotates the token, so
        // this is the one place the server hears about a new address.
        try {
          await fetch("/api/push/register", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ token: value, platform: "ios", appVersion: opts.appVersion }),
            credentials: "include",
          })
        } catch (err) {
          console.warn("[push] could not send token to server:", err)
        }
      }),
    )

    handles.push(
      await PushNotifications.addListener("registrationError", (err) => {
        // Almost always a provisioning problem — no push capability on the
        // App ID, or a build signed for the wrong APNs environment.
        console.warn("[push] APNs registration error:", err)
      }),
    )

    handles.push(
      await PushNotifications.addListener("pushNotificationActionPerformed", ({ notification }) => {
        // The tap. `path` is what lib/push/apns.ts puts alongside `aps`.
        const path = (notification.data as Record<string, unknown> | undefined)?.path
        if (typeof path === "string" && path.startsWith("/")) opts.onOpen(path)
      }),
    )
  } catch (err) {
    console.warn("[push] listeners failed:", err)
  }

  return () => {
    void Promise.all(handles.map((h) => h.remove().catch(() => {})))
  }
}

/** Sign-out: stop sending to this device. */
export async function forgetDevice(): Promise<void> {
  if (!isNativeApp()) return
  try {
    await fetch("/api/push/register", { method: "DELETE", credentials: "include" })
    await PushNotifications.removeAllDeliveredNotifications()
  } catch {
    // Best effort. A token nobody deletes is swept when APNs answers 410.
  }
}
